import {
	type Action,
	type BoundActions,
	createActionsHook,
	createHook,
	createSelector,
	createStore,
    type HookActionsFunction,
    type HookFunction,
} from 'react-sweet-state';

type SpotlightState = {
	spotlight:
		| {
				state: 'hidden';
		  }
		| {
				activeSpotlight: number;
				state: 'visible';
		  }
		| {
				state: 'seen';
		  }
		| {
				state: 'dismiss';
		  };
};

const initialState: SpotlightState = {
	spotlight: { state: 'hidden' },
};

const actions = {
	start:
		(): Action<SpotlightState> =>
		({ getState, setState }) => {
			const { spotlight } = getState();
			if (spotlight.state !== 'hidden') {
				return;
			}

			setState({ spotlight: { state: 'visible', activeSpotlight: 0 } });
		},
	next:
		(): Action<SpotlightState> =>
		({ setState, getState }) => {
			const { spotlight } = getState();
			if (spotlight.state !== 'visible') {
				return;
			}

			setState({ spotlight: { state: 'visible', activeSpotlight: spotlight.activeSpotlight + 1 } });
		},
	end:
		(): Action<SpotlightState> =>
		({ setState }) => {
			setState({ spotlight: { state: 'seen' } });
		},
	dismiss:
		(): Action<SpotlightState> =>
		({ setState }) => {
			setState({ spotlight: { state: 'dismiss' } });
		},
};

const SpotlightStore = createStore({
	initialState,
	actions,
	name: 'SpotlightStore',
});

export const useSpotlightActions: HookActionsFunction<BoundActions<SpotlightState, {
    dismiss: () => Action<SpotlightState>;
    end: () => Action<SpotlightState>;
    next: () => Action<SpotlightState>;
    start: () => Action<SpotlightState>;
}>> = createActionsHook(SpotlightStore);

export const useSpotlightStore: HookFunction<SpotlightState, BoundActions<SpotlightState, {
    dismiss: () => Action<SpotlightState>;
    end: () => Action<SpotlightState>;
    next: () => Action<SpotlightState>;
    start: () => Action<SpotlightState>;
}>, void> = createHook(SpotlightStore);

export const useSpotlightState: HookFunction<{
    state: "hidden";
} | {
    activeSpotlight: number;
    state: "visible";
} | {
    state: "seen";
} | {
    state: "dismiss";
}, BoundActions<SpotlightState, {
    dismiss: () => Action<SpotlightState>;
    end: () => Action<SpotlightState>;
    next: () => Action<SpotlightState>;
    start: () => Action<SpotlightState>;
}>, void> = createHook(SpotlightStore, {
	selector: createSelector(
		(state) => state.spotlight,
		(spotlightState) => spotlightState,
	),
});
